// COP — GaussianCloudLayer : the fragment-shader RADIAL GAUSSIAN uncertainty smear.
//
// This is the 0.1b HERO layer (DESIGN-SPEC §2 + §5 build-step 3). It is the
// TypeScript port of the PROVEN prototype
// (docs/ux/prototypes/smear-real/cloudLayer.js) onto the production deck.gl v9
// backend (deckBackend.ts). The prototype hit 71-103fps at 200 movers; the GLSL
// is carried over verbatim because the installed deck.gl (9.3.4) ScatterplotLayer
// fragment shader still exposes the same hooks (vFillColor / unitPosition /
// fragColor) and luma.gl (9.3.5) still parses the vs/fs #decl + #main-end inject
// keys — verified against node_modules before this port.
//
// WHAT IT DRAWS — one instanced quad per track, painting a true radial Gaussian
// in the FRAGMENT SHADER (never a CPU-tessellated outline / wireframe ellipse):
//
//     alpha(r) = peakAlpha * exp( -(r / sigma)^2 / 2 )
//
// where r is the normalized distance from the track anchor (0 center .. 1 at the
// quad edge = the cloud's geographic reach). Every pixel is GPU-computed, so the
// cloud is genuinely smooth (no faceting), composites softly where tracks overlap,
// and costs one quad per track — trivial inside the 16.7ms/60fps budget.
//
// CHANNEL LAW, enforced in the shader (DESIGN-CONTEXT §2.2 — the two MUST NOT cross):
//   - instancePeak  (peakAlpha) <- STALENESS  : scales alpha ONLY. A faded cloud
//                                  is faded because it is OLD. Touches nothing else.
//   - instanceSigma (sigma)     <- CONFIDENCE : crisp tight core (small sigma) =
//                                  high conf; wide soft falloff (large sigma) = low
//                                  conf. Edge-hardness. NEVER touches alpha.
//   - getRadius (reach, METRES) <- CONFIDENCE : the cloud's geographic extent. Pure
//                                  function of confidence (see deckBackend.smearGeometry);
//                                  age does NOT enter reach (the reach-bleed fix).
//                                  Because deck.gl projects getRadius in meters, the
//                                  cloud pans + ZOOMS WITH the map — a real ground area.
//
// We subclass ScatterplotLayer purely to inherit its battle-tested geo-projection +
// instancing machinery (getPosition in lng/lat, getRadius in meters, the
// MapboxOverlay interleaved pipeline). We replace its disk fragment with our
// Gaussian and add two per-instance attributes (sigma, peak) via
// getShaders()/initializeState() — the canonical deck.gl custom-shader pattern.

import { ScatterplotLayer } from "@deck.gl/layers";
import type { ScatterplotLayerProps } from "@deck.gl/layers";
import type { Accessor, DefaultProps } from "@deck.gl/core";

/** Extra per-instance accessors the Gaussian shader reads, layered on top of
 *  ScatterplotLayer's own props:
 *   - getSigma   : CONFIDENCE channel — Gaussian width as a fraction of reach.
 *   - getPeak    : STALENESS channel — peak alpha multiplier.
 *   - getAspect  : MOTION-ELLIPSE — along/cross aspect ratio (>=1). 1 = isotropic (default,
 *                  byte-identical to the legacy radial cloud); >1 ELONGATES the Gaussian
 *                  ALONG the heading (directional uncertainty). EXTENT only — never alpha.
 *   - getHeading : MOTION-ELLIPSE — the long-axis bearing (deg) the ellipse is rotated to.
 */
export type GaussianCloudLayerProps<DataT = unknown> =
  ScatterplotLayerProps<DataT> & {
    /** Gaussian sigma as a fraction of reach (CONFIDENCE only): small = crisp/tight. */
    getSigma?: Accessor<DataT, number>;
    /** Peak alpha multiplier (STALENESS only): smaller = older/dimmer. */
    getPeak?: Accessor<DataT, number>;
    /** Along/cross aspect ratio of the ellipse (>=1). 1 = isotropic; >1 stretches along heading. */
    getAspect?: Accessor<DataT, number>;
    /** Ellipse long-axis bearing (deg). The cloud's elongation rotates to this heading. */
    getHeading?: Accessor<DataT, number>;
  };

const defaultProps: DefaultProps<GaussianCloudLayerProps> = {
  ...ScatterplotLayer.defaultProps,
  getSigma: { type: "accessor", value: 0.5 },
  getPeak: { type: "accessor", value: 1.0 },
  // Default aspect 1 + heading 0 ⇒ the fragment reduces EXACTLY to the legacy radial
  // Gaussian (rotation of an isotropic field is a no-op), so an un-elongated cloud is
  // pixel-identical to before this change — the motion-ellipse is a strict superset.
  getAspect: { type: "accessor", value: 1.0 },
  getHeading: { type: "accessor", value: 0.0 },
};

/** ScatterplotLayer whose fragment is a radial Gaussian instead of a hard disk.
 *  See the module header for the channel law the GLSL enforces. */
export class GaussianCloudLayer<DataT = unknown> extends ScatterplotLayer<
  DataT,
  Required<GaussianCloudLayerProps<DataT>>
> {
  static override layerName = "GaussianCloudLayer";
  static override defaultProps = defaultProps;

  override getShaders() {
    const shaders = super.getShaders();
    return {
      ...shaders,
      inject: {
        // --- vertex: pass our per-instance attributes to the fragment stage ---
        // sigma + peak (the two channels) plus the motion-ellipse attributes (aspect +
        // the heading's sin/cos, precomputed ONCE per instance in the vertex stage so the
        // fragment never runs trig per-pixel).
        "vs:#decl": `
          in float instanceSigma;
          in float instancePeak;
          in float instanceAspect;
          in float instanceHeading;
          out float vSigma;
          out float vPeak;
          out float vAspect;
          out float vSin;
          out float vCos;
        `,
        "vs:#main-end": `
          vSigma = instanceSigma;
          vPeak = instancePeak;
          vAspect = max(instanceAspect, 1.0);
          float hr = radians(instanceHeading);
          vSin = sin(hr);
          vCos = cos(hr);
        `,
        // --- fragment: paint the (optionally anisotropic) Gaussian, OVERRIDING the disk ---
        "fs:#decl": `
          in float vSigma;
          in float vPeak;
          in float vAspect;
          in float vSin;
          in float vCos;
        `,
        // ScatterplotLayer's fragment computes vFillColor + a hard/AA disk into
        // fragColor (ending in DECKGL_FILTER_COLOR). We run AFTER it (#main-end)
        // and fully replace fragColor with the Gaussian, so none of the disk's
        // hard edge survives.
        "fs:#main-end": `
          // unitPosition is the fragment's position in the quad, -1..1.
          // raw length drives the EDGE WINDOW (cut at the square quad boundary) so the
          // feather is honest regardless of the ellipse rotation/stretch below.
          float rawLen = length(unitPosition);

          // MOTION-ELLIPSE: rotate unitPosition into the (along,cross) heading frame, then
          // DISCOUNT the along-track distance by the aspect ratio so the Gaussian falls off
          // more SLOWLY along heading (== elongated along heading). aspect 1 ⇒ along==cross
          // and this reduces to the isotropic length (a rotation preserves length), i.e. the
          // legacy radial cloud exactly. EXTENT only — alpha (vPeak) is untouched.
          float along = unitPosition.x * vSin + unitPosition.y * vCos;
          float across = unitPosition.x * vCos - unitPosition.y * vSin;
          float alongScaled = along / max(vAspect, 1.0);
          float r = sqrt(alongScaled * alongScaled + across * across);

          // sigma (vSigma) is the Gaussian width as a fraction of reach. Small sigma
          // => a crisp, tight core (high confidence). Large sigma => a wide, soft
          // falloff that fills the reach (low confidence). CONFIDENCE channel ONLY.
          float s = max(vSigma, 0.04);
          float g = exp(-(r * r) / (2.0 * s * s));

          // a gentle window so the Gaussian's mathematical tail is cut cleanly at
          // the quad edge (no hard square corner ever shows): feather the last 14%.
          // Keyed on the RAW (un-rotated) radius so the square quad boundary is the cut.
          float window = 1.0 - smoothstep(0.86, 1.0, rawLen);
          g *= window;

          // peakAlpha (vPeak) is the STALENESS channel: it scales the whole cloud's
          // alpha and nothing else. A faded cloud is faded because it is OLD.
          float a = g * vPeak;

          // soft floor cull so we don't pay for invisible fragments.
          if (a < 0.003) discard;

          // cloud body carries the per-track AFFILIATION hue via vFillColor.rgb
          // (the host sets getFillColor to the affiliation colour) so a hostile
          // track's uncertainty reads red, a friend's green — matching its frame.
          // The MEANING is still in alpha (age) + reach/sigma (confidence); hue only
          // CONFIRMS which track owns the cloud (the frame carries affiliation by
          // SHAPE for the MONO master test). a*vFillColor.a keeps the alpha story.
          fragColor = vec4(vFillColor.rgb, a * vFillColor.a);
        `,
      },
    };
  }

  override initializeState(): void {
    super.initializeState();
    const am = this.getAttributeManager();
    // getAttributeManager() is non-null for any instanced layer (ScatterplotLayer
    // is one); guard anyway so the types stay honest.
    if (!am) return;
    am.addInstanced({
      instanceSigma: {
        size: 1,
        accessor: "getSigma",
        defaultValue: 0.5,
      },
      instancePeak: {
        size: 1,
        accessor: "getPeak",
        defaultValue: 1.0,
      },
      // MOTION-ELLIPSE attributes. Defaults (aspect 1, heading 0) make the cloud isotropic
      // — identical to the legacy radial Gaussian — so omitting these accessors is safe.
      instanceAspect: {
        size: 1,
        accessor: "getAspect",
        defaultValue: 1.0,
      },
      instanceHeading: {
        size: 1,
        accessor: "getHeading",
        defaultValue: 0.0,
      },
    });
  }
}
